import React from "react";
import { useTranslation } from "react-i18next";
import { ServiceTypeSelectorOption } from "../../../../../../src/types/order";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import {
  buildAppUrl,
  checkIfOrderCanBeStarted,
  hasOpenBalance,
  isVenueReviewPending,
} from "../../../../../util";
import routes from "../../../../../routes/routes";
import { spaceOf } from "../../../../../util/useCases";

// Props for BookingSummaryNote
type BookingSummaryNoteProps = {
  serviceType: ServiceTypeSelectorOption;
};

function BookingSummaryNote({ serviceType }: BookingSummaryNoteProps) {
  const { t } = useTranslation();
  const isStayFlow = spaceOf(serviceType)?.flow === "stay";
  const isSUP = serviceType === ServiceTypeSelectorOption.SUP;
  const isEventFlow = spaceOf(serviceType)?.flow === "event";
  const isKeys = serviceType === ServiceTypeSelectorOption.KEYS;

  const { selectedBooking } = useSelector(
    (state: RootState) => state.booking
  );

  const isRentalFinished = selectedBooking?.items?.[0]?.rental.finishedAtDate
  const gracePeriodMinutes = useSelector(
    (state: RootState) => state.tenantConfig.config.gracePeriodMinutes

  );

  // Early return if SUP and note should not be shown
  if (isSUP && isRentalFinished) {
    return null;
  }

  // #2352: a venue reservation nobody has decided on yet is NOT an open
  // balance, so it must not be intercepted by the branch below. Nothing is
  // owed — no offer has been made — there is no payment link to point at, and
  // the "Buchungsänderung" mail that note names does not exist for a request
  // the customer has just submitted. What that state is owed is the
  // review-pending note further down.
  //
  // `hasOpenBalance` itself is correct and stays untouched (#1704: a concluded
  // postpay booking owes money and is still entitled to check in). What was
  // wrong is counting an undecided request as a balance at all.
  const venueAwaitsDecision =
    isEventFlow && isVenueReviewPending(selectedBooking ?? {});

  // An open balance outranks every other note: #2048 disables check-in while
  // money is owed, and a dead button with no explanation is worse than no
  // block at all. The customer is told what is missing and where the payment
  // link is.
  //
  // The link itself is not shown here. It is minted per e-mail and never
  // persisted, so putting it on the card needs either a stored link or an
  // on-demand mint endpoint (#2049) — this points at the mail the customer
  // already has instead of leaving them at a dead end.
  //
  // The amount is not shown either: it is not in this payload and cannot be
  // derived here, because the frontend never computes money
  // (booking-price-computation.md rule 3).
  if (
    !venueAwaitsDecision &&
    selectedBooking &&
    hasOpenBalance(selectedBooking)
  ) {
    return (
      <div className="bg-appLightBrown rounded-lg p-4 text-sm">
        <b>{t("orders.ticket_overview.open_balance.title")}</b>{" "}
        {t("orders.ticket_overview.open_balance.content")}
      </div>
    );
  }

  // Determine the content for the note
  let noteContent: string;
  if (isSUP) {
    noteContent = checkIfOrderCanBeStarted(selectedBooking!, gracePeriodMinutes)
      ? t("orders.ticket_overview.generic_note.sup_content")
      : t("orders.ticket_overview.generic_note.content");
  } else if (isStayFlow) {
    noteContent = t("orders.ticket_overview.apartment_overview_note.content");
  } else if (isEventFlow) {
    // #2352: this note tells the customer their reservation is still being
    // reviewed. Rendering it off the service type alone made it outlive the
    // review — a booking whose contract the customer had just concluded showed
    // the "Gebucht" chip and, directly below it, that it was awaiting approval.
    // A decided reservation gets no note: what happens next (the invoice, the
    // payment term) was already stated on the acceptance screen and in the
    // mail, so there is nothing to say here.
    if (!venueAwaitsDecision) {
      return null;
    }
    noteContent = t("orders.ticket_overview.venue_review_pending_note.content");
  }
  else if(isKeys){
    noteContent = t("orders.ticket_overview.generic_note.keys_content");
  }

   else {
    noteContent = t("orders.ticket_overview.generic_note.content");
  }

  return (
    <div className="bg-appLightBlue2 rounded p-2 text-sm mb-2 w-full">
      <span className="font-bold">
      {t("orders.ticket_overview.apartment_overview_note.nb")}
      </span>
      {noteContent}
      {/* `buildAppUrl`, not a bare path: on the shared deployment the app is
          mounted under `/{slug}` and a raw anchor bypasses React Router's
          basename, so this href dropped the tenant and landed on "This booking
          page doesn't exist" — with kolula's support address on it instead of
          the operator's. The same defect was found on the storage welcome
          screen at KGSt-Forum (2026-09-14); this is its sibling, and it is
          broken for every runtime tenant today. Kept as a link (it opens in a
          new tab, which the dialog on the welcome screen cannot do). */}
      <a
      href={buildAppUrl(routes.makeHintsPath("HelpAccordionList"))}
      className="ml-1 underline"
      target="_blank"
      rel="noopener noreferrer"
      >
      {t("report_issue.help.report_issue")}
      </a>
    </div>
  );
}

export default BookingSummaryNote;

